import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Fade,
  IconButton,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";

interface Props {
  open: boolean;
  coName: string;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

/**
 * Spec §6: "Approval = Digital Signature." Confirmation modal shown before the CO's
 * approval is committed. Surfaces who is signing and when.
 */
export function ApprovalSignatureDialog({
  open,
  coName,
  onClose,
  onConfirm,
  loading,
}: Props) {
  const theme = useTheme();
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      aria-labelledby="approval-dialog-title"
      aria-describedby="approval-dialog-description"
      TransitionComponent={Fade}
      transitionDuration={200}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3,
          minWidth: 380,
          maxWidth: 480,
          overflow: "visible",
          border: "1px solid",
          borderColor: "divider",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
          },
        },
      }}
    >
      <IconButton
        aria-label="Close"
        onClick={onClose}
        disabled={loading}
        size="small"
        sx={{
          position: "absolute",
          right: 12,
          top: 12,
          color: "text.secondary",
          "&:hover": { backgroundColor: "action.hover" },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogContent sx={{ pt: 4, pb: 2, px: 4, textAlign: "center" }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            backgroundColor: "success.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px",
            boxShadow: `0 4px 14px ${alpha(theme.palette.success.main, 0.3)}`,
          }}
        >
          <VerifiedOutlinedIcon sx={{ fontSize: 32, color: "white" }} />
        </Box>

        <Typography
          id="approval-dialog-title"
          variant="h5"
          component="h2"
          sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}
        >
          Confirm approval
        </Typography>

        <Typography
          id="approval-dialog-description"
          variant="body1"
          sx={{ color: "text.secondary", lineHeight: 1.6 }}
        >
          By approving, you attest that you have reviewed this report and accept
          it for submission to RMS.{" "}
          <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
            This action is recorded as your supervisory signature.
          </Box>
        </Typography>

        <Box
          sx={{
            mt: 3,
            p: 1.75,
            borderRadius: 2,
            border: `1px solid ${theme.palette.success.main}`,
            bgcolor: alpha(theme.palette.success.main, 0.06),
            textAlign: "left",
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            Signing as
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            {coName}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mt={0.5}
          >
            {dayjs().format("MMMM D, YYYY · h:mm A")}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          pb: 4,
          pt: 2,
          gap: 1.5,
          flexDirection: "column",
          alignItems: "stretch",
          "& > :not(:first-of-type)": { marginLeft: 0 },
        }}
      >
        <Button
          onClick={onConfirm}
          color="success"
          variant="contained"
          startIcon={<VerifiedOutlinedIcon />}
          loading={loading}
          fullWidth
          disableElevation
          autoFocus
          sx={{
            py: 1.25,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.95rem",
            fontWeight: 600,
            "&:hover": { backgroundColor: "success.dark" },
          }}
        >
          Approve &amp; sign
        </Button>
        <Button
          onClick={onClose}
          disabled={loading}
          color="inherit"
          variant="text"
          fullWidth
          sx={{
            py: 1.25,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.95rem",
            fontWeight: 500,
            color: "text.secondary",
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
